<template>
  <h1 class="heading-1 mb-16">
    {{ i18n(translations.title) }}
  </h1>

  <div v-if="!hasKybVerified">
    <FormattedMessage class="mb-7" :definition="translations.subtitle" tag="p">
      <template #link>
        <RevLink target="_blank" :to="HYPERWALLET_ABOUT_LINK">
          {{ i18n(translations.learnMoreAboutHyperwallet) }}
        </RevLink>
      </template>
    </FormattedMessage>
    <RevCard class="mx-0 mb-28 p-24 md:p-28">
      <div id="hyperwalletTransferMethodsUI" />
    </RevCard>
  </div>

  <div v-else>
    {{ i18n(translations.alreadyConfigured) }}
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useHead } from '#imports'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import {
  getHyperwalletAccessToken,
  getHyperwalletTransferMethodsUrl,
  postHyperwalletTransferMethod,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/hyperwallet'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'

import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { HYPERWALLET_ABOUT_LINK } from './TransferMethods.constants'
import translations from './TransferMethods.translations'

const i18n = useI18n()
const { hasKybVerified } = useSellerInfoStore()
const { openErrorToast } = useTheToast()

const timeout = ref(null as ReturnType<typeof setTimeout> | null)

const { data: hyperwalletTransferMethodsPayload } = await useHttpFetch(
  getHyperwalletTransferMethodsUrl,
)

const transferMethodsScriptLink = computed(
  () => hyperwalletTransferMethodsPayload.value?.scriptLink,
)

const configuration = computed(
  () => hyperwalletTransferMethodsPayload.value?.configuration,
)

useHead({
  title: i18n(translations.title),
  script: [{ src: transferMethodsScriptLink.value?.href }],
})

function setupHyperwallet() {
  if (!hasKybVerified) {
    window.HWWidgets?.initialize(
      (
        onSuccess: (token: string | undefined) => void,
        onFailure: (error: Error) => void,
      ) => {
        $httpFetch(getHyperwalletAccessToken)
          .then(({ accessToken }) => {
            onSuccess(accessToken)
          })
          .catch((err) => onFailure(err))
      },
    )

    window.HWWidgets.transferMethods
      .configure({
        template: 'plain',
        el: document.getElementById('hyperwalletTransferMethodsUI'),
      })
      .create(configuration.value)

    window.HWWidgets.events.on(
      'widget:transfermethods:completed',
      (transferMethod: string) => {
        $httpFetch(postHyperwalletTransferMethod, {
          body: { trmObject: JSON.stringify(transferMethod) },
        }).then(
          async () =>
            await navigateTo({
              name: ROUTE_NAMES.OPTIONS.HYPERWALLET.CONFIRMATION,
            }),
        )
      },
    )
  }
}

function checkHyperwalletAvailability() {
  if (window.HWWidgets) {
    setupHyperwallet()
  } else {
    timeout.value = setTimeout(checkHyperwalletAvailability, 500)
  }
}

// clear au unmount
onUnmounted(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})

onMounted(async () => {
  if (!hasKybVerified) {
    // For now, we got issues with IT accounts on HW, the drop-in-UI request fails : there is no script link to handle
    if (!transferMethodsScriptLink.value) {
      openErrorToast({
        content: i18n(translations.hWErrorToastContent),
      })

      // As we cannot display the HW page, we go back to home, the error toasts is still visible though
      await navigateTo({ name: ROUTE_NAMES.ONBOARDING.HOME })
    } else if (transferMethodsScriptLink.value) {
      // We need to check if the Hyperwallet external script has run completely before setting up dropin UI
      checkHyperwalletAvailability()
    }
  }
})
</script>

<style lang="css">
/*
*  Hyperwallet sends us there own HTML, we need to use plain
*  css to target tags and selectors to apply our BM styles
*/
#hyperwalletTransferMethodsUI {
  font-family: BodyFont, sans-serif;
  font-weight: normal;
  color: #111111;
}

#hyperwalletTransferMethodsUI h2.hw-field-headers {
  font-size: 22px;
  line-height: 32px;
  margin: 8px 0;
}

#hyperwalletTransferMethodsUI h3.hw-field-headers {
  font-size: 20px;
  line-height: 28px;
  margin: 32px 0 16px 0;
}

#hyperwalletTransferMethodsUI .hw-custom-transfermethodtype-details p {
  margin: 0;
}

#hyperwalletTransferMethodsUI
  .hw-custom-transfermethodtype-details
  p:first-of-type {
  margin-bottom: 16px;
}

#hyperwalletTransferMethodsUI .hw-fields {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  height: 100%;
}

#hyperwalletTransferMethodsUI .hw-fields label {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 18px;
  font-weight: normal;
}

#hyperwalletTransferMethodsUI .hw-fields input {
  width: 100%;
  height: 48px;
  padding: 14px 16px 14px 12px;
  font: inherit;
  color: inherit;
  background-color: inherit;
  outline: none;
  border-radius: 6px;
  border: 1px solid #e2e2e2;
  background-color: #fff;
}

#hyperwalletTransferMethodsUI .hw-fields input:-moz-ui-invalid {
  box-shadow: none;
}

#hyperwalletTransferMethodsUI .hw-fields input:focus {
  padding-right: 8px;
  color: #555555;
}

/* Get rid of the light blue background color when autofill is used in Chrome. */
#hyperwalletTransferMethodsUI .hw-fields input:-webkit-autofill,
#hyperwalletTransferMethodsUI .hw-fields input:-webkit-autofill:hover,
#hyperwalletTransferMethodsUI .hw-fields input:-webkit-autofill:focus,
#hyperwalletTransferMethodsUI .hw-fields input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0px 1000px #fff inset !important;
}

/* SELECT */
#hyperwalletTransferMethodsUI .hw-fields select {
  height: 48px;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  background-color: #fff;
  padding: 14px 12px;
  flex-grow: 1;
}

/* BUTTONS */
#hyperwalletTransferMethodsUI .hw-tmc-button-group {
  margin-top: 16px;
}

/* ERRORS */
#hyperwalletTransferMethodsUI .has-error .hw-error-message {
  color: hsla(358, 84%, 36%, 1);
}

#hyperwalletTransferMethodsUI button {
  -webkit-appearance: none;
  border: none;
  text-decoration: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 16px 24px;
  margin-top: 16px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 500;
  line-height: 20px;
  font-size: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
#hyperwalletTransferMethodsUI button:disabled {
  cursor: not-allowed;
}

/* BUTTON PRIMARY */
#hyperwalletTransferMethodsUI .hw-field-buttons {
  background-color: #111;
  color: #f9f9f9;
}
#hyperwalletTransferMethodsUI .hw-field-buttons:hover {
  text-decoration: none;
  background: #535353;
}
#hyperwalletTransferMethodsUI .hw-field-buttons:disabled {
  background: #f1f1f1;
  color: #989898;
}
#hyperwalletTransferMethodsUI .hw-field-buttons:focus {
  outline: none;
  background: #535353;
}
#hyperwalletTransferMethodsUI .hw-field-buttons::-moz-focus-inner {
  border: none;
}
/* BUTTON SECONDARY */
#hyperwalletTransferMethodsUI .hw-tmc-back-button {
  background-color: transparent;
  color: #111;
  border: 1px solid #999;
}
#hyperwalletTransferMethodsUI .hw-tmc-back-button:hover,
#hyperwalletTransferMethodsUI .hw-tmc-back-button:focus {
  background-color: #f1f1f1;
}
#hyperwalletTransferMethodsUI .hw-tmc-back-button:focus {
  border-color: #535353;
}
#hyperwalletTransferMethodsUI .hw-tmc-back-button:disabled {
  color: #999;
}
</style>
